import type { CSSProperties } from 'react';

export type ProjectTheme = {
  accent: string;
  stageColor: string;
  inkColor: string;
  fadedInk: string;
  detailMuted: string;
  detailColor: string;
  linkRule: string;
};

export const projectThemes: Record<
  'freshmark' | 'netflix' | 'unwrapLove' | 'dimensionalGate' | 'ensnared',
  ProjectTheme
> = {
  /* Light gallery-wall stages: high-lightness tinted walls per project, heavy
     ink type, one deep same-hue accent for chips/links. HECO structure kept
     (full-bleed stage swap, ghost heading, pills) at the site's own softness. */
  freshmark: {
    accent: '#8a6414',
    stageColor: '#efe5cf',
    inkColor: '#262012',
    fadedInk: 'rgba(38, 32, 18, 0.16)',
    detailMuted: 'rgba(38, 32, 18, 0.64)',
    detailColor: 'rgba(38, 32, 18, 0.78)',
    linkRule: 'rgba(38, 32, 18, 0.32)',
  },
  netflix: {
    accent: '#b03a28',
    stageColor: '#f0d0c0',
    inkColor: '#2a1713',
    fadedInk: 'rgba(42, 23, 19, 0.16)',
    detailMuted: 'rgba(42, 23, 19, 0.64)',
    detailColor: 'rgba(42, 23, 19, 0.78)',
    linkRule: 'rgba(42, 23, 19, 0.32)',
  },
  unwrapLove: {
    accent: '#5c3d68',
    stageColor: '#e0d2ec',
    inkColor: '#201a2b',
    fadedInk: 'rgba(32, 26, 43, 0.16)',
    detailMuted: 'rgba(32, 26, 43, 0.64)',
    detailColor: 'rgba(32, 26, 43, 0.78)',
    linkRule: 'rgba(32, 26, 43, 0.32)',
  },
  dimensionalGate: {
    accent: '#33506b',
    stageColor: '#cfdeea',
    inkColor: '#16202b',
    fadedInk: 'rgba(22, 32, 43, 0.16)',
    detailMuted: 'rgba(22, 32, 43, 0.64)',
    detailColor: 'rgba(22, 32, 43, 0.78)',
    linkRule: 'rgba(22, 32, 43, 0.32)',
  },
  ensnared: {
    accent: '#5d7226',
    stageColor: '#e0e6c4',
    inkColor: '#212910',
    fadedInk: 'rgba(33, 41, 16, 0.16)',
    detailMuted: 'rgba(33, 41, 16, 0.64)',
    detailColor: 'rgba(33, 41, 16, 0.78)',
    linkRule: 'rgba(33, 41, 16, 0.32)',
  },
};

type ProjectDetailThemeStyle = CSSProperties & {
  '--project-accent': string;
  '--project-sidebar-accent': string;
  '--project-sidebar': string;
  '--project-surface': string;
  '--project-ink': string;
  '--project-muted': string;
  '--project-rule': string;
};

const toDetailThemeStyle = (theme: ProjectTheme): ProjectDetailThemeStyle => ({
  '--project-accent': theme.accent,
  '--project-sidebar-accent': theme.accent,
  '--project-sidebar': theme.stageColor,
  '--project-surface': theme.stageColor,
  '--project-ink': theme.inkColor,
  '--project-muted': theme.detailColor,
  '--project-rule': theme.linkRule,
});

export const projectDetailThemeStyles = {
  freshmark: {
    ...toDetailThemeStyle(projectThemes.freshmark),
    '--project-surface': '#faf7ef',
    '--project-rule': 'rgba(138, 100, 20, 0.25)',
  },
  netflix: {
    ...toDetailThemeStyle(projectThemes.netflix),
    '--project-accent': '#f45946',
    '--project-sidebar-accent': '#141414',
    '--project-surface': '#faf1ef',
    '--project-rule': 'rgba(176, 58, 40, 0.22)',
  },
  unwrapLove: {
    ...toDetailThemeStyle(projectThemes.unwrapLove),
    '--project-surface': '#f8f4f9',
    '--project-rule': 'rgba(92, 61, 104, 0.22)',
  },
  dimensionalGate: {
    ...toDetailThemeStyle(projectThemes.dimensionalGate),
    '--project-surface': '#f1f5f8',
    '--project-rule': 'rgba(51, 80, 107, 0.22)',
  },
  ensnared: {
    ...toDetailThemeStyle(projectThemes.ensnared),
    '--project-surface': '#f8f9f0',
    '--project-rule': 'rgba(93, 114, 38, 0.25)',
  },
};
